import {
  mapExtractedRecipe,
  parseSchemaOrgRecipe,
  planRecipeImport,
  type CollectionId,
  type RecipeStore,
} from '@platewell/core';
import { extractedRecipeSchema, type ImportFileDto } from '@platewell/schema';
import {
  fetchImportFileContent,
  fetchImportFileResult,
  importRecipeImage,
  patchImportFile,
} from '../lib/api.js';
import { attachPhoto } from '../lib/uploads.js';

const DATA_URL_RE = /^data:([^;,]+)?(;base64)?,(.*)$/i;

function dataUrlToFile(url: string): File | null {
  const match = DATA_URL_RE.exec(url);
  if (match == null) return null;
  const mime = match[1] || 'application/octet-stream';
  const isBase64 = match[2] != null;
  const payload = match[3] ?? '';
  try {
    if (isBase64) {
      const binary = atob(payload);
      const bytes = new Uint8Array(binary.length);
      for (let i = 0; i < binary.length; i += 1) bytes[i] = binary.charCodeAt(i);
      return new File([bytes], 'imported-image', { type: mime });
    }
    return new File([decodeURIComponent(payload)], 'imported-image', { type: mime });
  } catch {
    return null;
  }
}

async function attachImportedImage(
  store: RecipeStore,
  recipeId: string,
  householdId: string,
  url: string,
): Promise<void> {
  if (url.startsWith('data:')) {
    const file = dataUrlToFile(url);
    if (file == null) return;
    await attachPhoto(store, recipeId, file, householdId);
    return;
  }

  const stored = await importRecipeImage(householdId, url);
  await store.addPhoto(recipeId, {
    blobId: stored.blobId,
    width: stored.width,
    height: stored.height,
  });
}

/** Files the client can commit into the CRDT right now. */
export function isClientProcessable(file: ImportFileDto): boolean {
  if (file.sourceKind === 'json') return file.status === 'pending';
  return file.status === 'ready';
}

export async function importOneFile(input: {
  store: RecipeStore;
  householdId: string;
  importId: string;
  file: ImportFileDto;
  localContent?: string;
  /** Skip duplicate detection once (user chose Force import). */
  force?: boolean;
  /** Where the recipe is filed. Defaults to the household's import collection. */
  collectionId?: CollectionId;
}): Promise<ImportFileDto> {
  const { store, householdId, importId, file } = input;
  const claimedId = input.force ? store.context.newId() : (file.recipeId ?? store.context.newId());

  await patchImportFile(householdId, importId, file.id, {
    status: 'in-progress',
    recipeId: claimedId,
  });

  try {
    const { recipe, imageUrls } = await resolveRecipeDraft(input);
    const plan = planRecipeImport(recipe, store.listRecipes(), {
      claimedId,
      force: input.force,
    });

    if (plan.kind === 'duplicate') {
      return await patchImportFile(householdId, importId, file.id, {
        status: 'duplicate',
        recipeId: plan.recipeId,
        error: `This recipe is ${Math.round(plan.score * 100)}% similar to an existing recipe`,
      });
    }

    if (plan.kind === 'create') {
      await store.addRecipe({ ...recipe, id: plan.recipeId }, [
        input.collectionId ?? store.getImportCollectionId(),
      ]);
    }

    const recipeId = plan.recipeId;
    for (const imageUrl of imageUrls) {
      try {
        await attachImportedImage(store, recipeId, householdId, imageUrl);
      } catch {
        // A missing image should not fail the recipe import.
      }
    }

    return await patchImportFile(householdId, importId, file.id, {
      status: 'success',
      recipeId,
    });
  } catch (error) {
    const message = error instanceof Error ? error.message : 'Import failed';
    return await patchImportFile(householdId, importId, file.id, {
      status: 'fail',
      error: message,
    });
  }
}

async function resolveRecipeDraft(input: {
  householdId: string;
  importId: string;
  file: ImportFileDto;
  localContent?: string;
}): Promise<{ recipe: ReturnType<typeof parseSchemaOrgRecipe>['recipe']; imageUrls: string[] }> {
  const { householdId, importId, file } = input;

  if (file.sourceKind === 'json') {
    const raw =
      input.localContent ?? (await fetchImportFileContent(householdId, importId, file.id));
    return parseSchemaOrgRecipe(JSON.parse(raw) as unknown, {
      filename: file.originalFilename,
      fileText: raw,
    });
  }

  const resultJson = await fetchImportFileResult(householdId, importId, file.id);
  const validated = extractedRecipeSchema.safeParse(JSON.parse(resultJson));
  if (!validated.success) {
    throw new Error('Extracted recipe result is invalid');
  }
  return mapExtractedRecipe(validated.data, {
    inputUrl: file.sourceKind === 'url' ? file.originalFilename : undefined,
    filename: file.originalFilename,
  });
}

export function importCompletionMessage(
  files: ImportFileDto[],
  collectionName?: string,
): string | null {
  if (files.length === 0) return null;
  const done = files.every(
    (file) => file.status === 'success' || file.status === 'fail' || file.status === 'duplicate',
  );
  if (!done) return null;
  const success = files.filter((file) => file.status === 'success').length;
  const fail = files.filter((file) => file.status === 'fail').length;
  const duplicates = files.filter((file) => file.status === 'duplicate').length;
  const parts: string[] = [];
  if (success > 0) {
    const noun = success === 1 ? 'recipe' : 'recipes';
    const into = collectionName != null ? ` into the "${collectionName}" collection` : '';
    parts.push(`Imported ${success} ${noun}${into}`);
  }
  if (duplicates > 0) {
    parts.push(duplicates === 1 ? '1 duplicate skipped' : `${duplicates} duplicates skipped`);
  }
  if (fail > 0) {
    parts.push(fail === 1 ? '1 failed' : `${fail} failed`);
  }
  if (parts.length === 0) return 'Nothing to import.';
  return `${parts.join('. ')}.`;
}
